/**
 * HTTP Client - Centralni fetch wrapper sa error handling
 */

import { getApiUrl } from '../../config/environment';
import { getAuthToken } from '../../core/auth/supabase-auth';
import { ApiResponse } from '../types/apiTypes';
import {
  handle403Error,
  AuthenticationError,
  logError,
} from '../utils/errorHandler';

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

/**
 * HTTP Client class for making API requests
 */
class HttpClient {
  /**
   * Get authorization headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getAuthToken();
    if (!token) {
      throw new AuthenticationError();
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Make a generic HTTP request
   */
  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { body, skipAuth = false, ...fetchOptions } = options;

    try {
      // Build headers
      const headers: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
      };

      // Add auth headers if needed
      if (!skipAuth) {
        const authHeaders = await this.getAuthHeaders();
        Object.assign(headers, authHeaders);
      }

      // Add content-type for JSON bodies
      if (body && !(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      // Build request
      const requestInit: RequestInit = {
        ...fetchOptions,
        headers,
      };

      // Add body
      if (body) {
        requestInit.body = body instanceof FormData ? body : JSON.stringify(body);
      }

      // Make request
      const response = await fetch(getApiUrl(endpoint), requestInit);

      // Handle 403 error
      await handle403Error(response);

      // Parse response
      const data = await response.json();

      // Check for error response
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || `Request failed with status ${response.status}`,
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      logError(`HTTP ${options.method || 'GET'} ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * POST with FormData (for file uploads)
   */
  async postFormData<T>(endpoint: string, formData: FormData, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: formData });
  }

  /**
   * GET request that returns a Blob (for file downloads)
   */
  async getBlob(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<Blob> {
    const { skipAuth = false, ...fetchOptions } = options || {};

    try {
      const headers: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
      };

      if (!skipAuth) {
        const authHeaders = await this.getAuthHeaders();
        Object.assign(headers, authHeaders);
      }

      const response = await fetch(getApiUrl(endpoint), {
        ...fetchOptions,
        method: 'GET',
        headers,
      });

      await handle403Error(response);

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      return await response.blob();
    } catch (error) {
      logError(`HTTP GET BLOB ${endpoint}`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

// Export class for testing
export { HttpClient };
